// Logging utility for the frontend application
// Provides structured logging with different levels and formatting

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: string;
  data?: unknown;
  userId?: string;
  sessionId?: string;
}

export class Logger {
  private static instance: Logger;
  private logLevel: LogLevel = 'info';
  private sessionId: string;
  private logs: LogEntry[] = [];
  private maxLogEntries: number = 1000;

  private constructor() {
    this.sessionId = this.generateSessionId();
    this.setLogLevel();
  }

  public static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private generateSessionId(): string {
    return `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private setLogLevel(): void {
    const envLogLevel = import.meta.env.VITE_LOG_LEVEL as LogLevel;
    const validLevels: LogLevel[] = ['debug', 'info', 'warn', 'error'];

    if (envLogLevel && validLevels.includes(envLogLevel)) {
      this.logLevel = envLogLevel;
    } else {
      // Default to 'debug' in development, 'warn' in production
      this.logLevel = import.meta.env.DEV ? 'debug' : 'warn';
    }
  }

  private shouldLog(level: LogLevel): boolean {
    const levels: Record<LogLevel, number> = {
      debug: 0,
      info: 1,
      warn: 2,
      error: 3,
    };

    return levels[level] >= levels[this.logLevel];
  }

  private formatMessage(entry: LogEntry): string {
    const timestamp = entry.timestamp;
    const level = entry.level.toUpperCase().padEnd(5);
    const context = entry.context ? `[${entry.context}]` : '';
    const sessionId = `[${entry.sessionId}]`;

    return `${timestamp} ${level} ${sessionId} ${context} ${entry.message}`;
  }

  private createLogEntry(
    level: LogLevel,
    message: string,
    context?: string,
    data?: unknown,
    userId?: string
  ): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      message,
      context,
      data,
      userId,
      sessionId: this.sessionId,
    };
  }

  private logToConsole(entry: LogEntry): void {
    const formattedMessage = this.formatMessage(entry);

    switch (entry.level) {
      case 'debug':
        console.debug(formattedMessage, entry.data || '');
        break;
      case 'info':
        console.info(formattedMessage, entry.data || '');
        break;
      case 'warn':
        console.warn(formattedMessage, entry.data || '');
        break;
      case 'error':
        console.error(formattedMessage, entry.data || '');
        break;
    }
  }

  private storeLog(entry: LogEntry): void {
    this.logs.push(entry);

    // Keep only the last maxLogEntries entries
    if (this.logs.length > this.maxLogEntries) {
      this.logs = this.logs.slice(-this.maxLogEntries);
    }
  }

  public debug(
    message: string,
    context?: string,
    data?: unknown,
    userId?: string
  ): void {
    if (!this.shouldLog('debug')) return;

    const entry = this.createLogEntry('debug', message, context, data, userId);
    this.logToConsole(entry);
    this.storeLog(entry);
  }

  public info(
    message: string,
    context?: string,
    data?: unknown,
    userId?: string
  ): void {
    if (!this.shouldLog('info')) return;

    const entry = this.createLogEntry('info', message, context, data, userId);
    this.logToConsole(entry);
    this.storeLog(entry);
  }

  public warn(
    message: string,
    context?: string,
    data?: unknown,
    userId?: string
  ): void {
    if (!this.shouldLog('warn')) return;

    const entry = this.createLogEntry('warn', message, context, data, userId);
    this.logToConsole(entry);
    this.storeLog(entry);
  }

  public error(
    message: string,
    context?: string,
    data?: unknown,
    userId?: string
  ): void {
    if (!this.shouldLog('error')) return;

    const entry = this.createLogEntry('error', message, context, data, userId);
    this.logToConsole(entry);
    this.storeLog(entry);
  }

  // User interaction logging
  public logUserInteraction(
    action: string,
    element?: string,
    data?: unknown,
    userId?: string
  ): void {
    const logData = {
      element,
      ...(data && typeof data === 'object' ? data : { data }),
    };
    this.info(`User interaction: ${action}`, 'UI', logData, userId);
  }

  // Get stored logs for debugging
  public getLogs(level?: LogLevel): LogEntry[] {
    if (level) {
      return this.logs.filter(log => log.level === level);
    }
    return [...this.logs];
  }

  // Clear stored logs
  public clearLogs(): void {
    this.logs = [];
  }

  // Get current log level
  public getLogLevel(): LogLevel {
    return this.logLevel;
  }

  // Set log level programmatically
  public setLogLevelManually(level: LogLevel): void {
    this.logLevel = level;
  }
}

// Export singleton instance
export const logger = Logger.getInstance();

// Convenience functions for common logging scenarios
export const logError = (error: Error, context?: string, userId?: string) => {
  logger.error(
    error.message,
    context,
    {
      name: error.name,
      stack: error.stack,
    },
    userId
  );
};

export const logApiCall = (
  method: string,
  url: string,
  status?: number,
  userId?: string
) => {
  const message = `API ${method.toUpperCase()} ${url}${
    status ? ` (${status})` : ''
  }`;
  const level = status && status >= 400 ? 'error' : 'info';
  logger[level](message, 'API', { method, url, status }, userId);
};

export const logUserAction = (
  action: string,
  component?: string,
  data?: unknown,
  userId?: string
) => {
  logger.logUserInteraction(action, component, data, userId);
};
